import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Users, Building2, Shield, Star, Sparkles, TrendingUp, Award, Zap, Target, Globe } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { features, courses } from '../lib/data';
export function HomePage() {
  const popularCourses = courses.filter(c => c.popular);
  return <div className="flex flex-col overflow-hidden" data-id="element-817">
      {/* Hero Section - Bold Asymmetric Design */}
      <section className="relative min-h-[90vh] flex items-center bg-gradient-to-br from-cream-100 via-white to-forest-50 overflow-hidden" data-id="element-818">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 bg-mesh" data-id="element-819"></div>
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-gradient-to-br from-ember-400/20 to-forest-500/20 rounded-full blur-3xl animate-float" data-id="element-820"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-forest-500/20 to-ember-400/20 rounded-full blur-3xl" style={{
        animationDelay: '3s'
      }} data-id="element-821"></div>

        {/* Diagonal Accent */}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-br from-forest-700 to-forest-900 transform skew-x-12 translate-x-1/4 opacity-5" data-id="element-822"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 z-10" data-id="element-823">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-824">
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} data-id="element-825">
              <Badge variant="gradient" className="mb-6 animate-fade-in" data-id="element-826">
                <Sparkles className="w-3 h-3 mr-1" data-id="element-827" />
                Ireland's #1 Online Food Safety Training
              </Badge>

              <h1 className="text-5xl md:text-7xl font-display font-bold leading-[1.1] mb-6" data-id="element-828">
                <span className="text-forest-900" data-id="element-829">Food Safety</span>
                <br data-id="element-830" />
                <span className="text-gradient" data-id="element-831">On Demand</span>
              </h1>

              <p className="text-xl text-gray-700 mb-8 leading-relaxed max-w-xl" data-id="element-832">
                Get certified from anywhere with Ireland's most trusted online
                HACCP and food safety training.
                <span className="font-semibold text-forest-700" data-id="element-833">
                  {' '}
                  Self-paced, certified, instant results.
                </span>
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-12" data-id="element-834">
                <Link to="/courses" data-id="element-835">
                  <Button size="lg" variant="primary" className="group" data-id="element-836">
                    Start Training Now
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-837" />
                  </Button>
                </Link>
                <Link to="/corporate" data-id="element-838">
                  <Button size="lg" variant="outline" data-id="element-839">
                    Team Accounts
                  </Button>
                </Link>
              </div>

              <div className="flex items-center space-x-8" data-id="element-840">
                <div className="flex items-center" data-id="element-841">
                  <div className="flex -space-x-2" data-id="element-842">
                    {[1, 2, 3, 4].map(i => <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-forest-400 to-forest-600 border-2 border-white flex items-center justify-center text-white text-xs font-bold" data-id="element-843">
                        {i}
                      </div>)}
                  </div>
                  <div className="ml-3" data-id="element-844">
                    <div className="flex text-ember-500" data-id="element-845">
                      {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 fill-current" data-id="element-846" />)}
                    </div>
                    <p className="text-xs text-gray-600 font-medium" data-id="element-847">
                      10,000+ certified online
                    </p>
                  </div>
                </div>

                <div className="h-12 w-px bg-gray-200" data-id="element-848"></div>

                <div data-id="element-849">
                  <p className="text-2xl font-display font-bold text-forest-800" data-id="element-850">
                    98%
                  </p>
                  <p className="text-xs text-gray-600 font-medium" data-id="element-851">Pass Rate</p>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="relative hidden lg:block" data-id="element-852">
              {/* Main Image Card */}
              <div className="relative" data-id="element-853">
                <div className="absolute -inset-4 bg-gradient-to-r from-forest-500 to-ember-500 rounded-3xl blur-2xl opacity-30 animate-pulse" data-id="element-854"></div>
                <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden border-4 border-white" data-id="element-855">
                  <img src="https://images.unsplash.com/photo-1556910103-1c02745a30bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Professional kitchen training" className="w-full h-[500px] object-cover" data-id="element-856" />
                  <div className="absolute inset-0 bg-gradient-to-t from-forest-900/80 via-forest-900/20 to-transparent" data-id="element-857"></div>
                </div>
              </div>

              {/* Floating Stats Cards */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.6,
              duration: 0.6
            }} className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-2xl p-6 border border-forest-100" data-id="element-858">
                <div className="flex items-center space-x-4" data-id="element-859">
                  <div className="w-12 h-12 bg-gradient-to-br from-ember-500 to-ember-600 rounded-xl flex items-center justify-center" data-id="element-860">
                    <Award className="w-6 h-6 text-white" data-id="element-861" />
                  </div>
                  <div data-id="element-862">
                    <p className="text-2xl font-display font-bold text-forest-900" data-id="element-863">
                      FSAI
                    </p>
                    <p className="text-xs text-gray-600 font-medium" data-id="element-864">
                      Certified Training
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.8,
              duration: 0.6
            }} className="absolute -top-6 -right-6 bg-gradient-to-br from-forest-700 to-forest-900 rounded-2xl shadow-2xl p-6 text-white" data-id="element-865">
                <div className="flex items-center space-x-3" data-id="element-866">
                  <TrendingUp className="w-8 h-8" data-id="element-867" />
                  <div data-id="element-868">
                    <p className="text-3xl font-display font-bold" data-id="element-869">500+</p>
                    <p className="text-xs opacity-80 font-medium" data-id="element-870">
                      Companies Trained
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section - Bento Grid Style */}
      <section className="py-24 bg-white relative overflow-hidden" data-id="element-871">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-forest-300 to-transparent" data-id="element-872"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-873">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-874">
            <Badge variant="outline" className="mb-4" data-id="element-875">
              <Zap className="w-3 h-3 mr-1" data-id="element-876" />
              Why Choose Us
            </Badge>
            <h2 className="text-4xl md:text-5xl font-display font-bold text-forest-900 mb-4" data-id="element-877">
              Training That{' '}
              <span className="text-gradient" data-id="element-878">Actually Works</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-879">
              We've reimagined food safety education for the modern era
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-880">
            {features.map((feature, index) => <motion.div key={feature.title} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="group relative bg-gradient-to-br from-cream-50 to-white rounded-2xl p-8 border-2 border-forest-100 hover:border-forest-300 transition-all duration-300 hover:shadow-xl hover:-translate-y-1" data-id="element-881">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-forest-500/5 to-ember-500/5 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" data-id="element-882"></div>

                <div className="relative" data-id="element-883">
                  <div className="w-14 h-14 bg-gradient-to-br from-forest-600 to-forest-700 rounded-2xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform shadow-lg" data-id="element-884">
                    <feature.icon className="w-7 h-7" data-id="element-885" />
                  </div>
                  <h3 className="text-xl font-display font-bold text-forest-900 mb-3" data-id="element-886">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-887">
                    {feature.description}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Popular Courses - Card Showcase */}
      <section className="py-24 bg-gradient-to-br from-forest-900 via-forest-800 to-forest-900 text-white relative overflow-hidden" data-id="element-888">
        <div className="absolute inset-0 opacity-10" data-id="element-889">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-ember-500 rounded-full blur-3xl" data-id="element-890"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-forest-500 rounded-full blur-3xl" data-id="element-891"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-892">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-16 gap-6" data-id="element-893">
            <div data-id="element-894">
              <Badge variant="warning" className="mb-4" data-id="element-895">
                <Star className="w-3 h-3 mr-1" data-id="element-896" />
                Most Popular
              </Badge>
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-4" data-id="element-897">
                Start Your Journey
              </h2>
              <p className="text-forest-200 text-lg max-w-xl" data-id="element-898">
                Industry-leading certification courses trusted by Ireland's top
                hospitality brands
              </p>
            </div>
            <Link to="/courses" data-id="element-899">
              <Button variant="secondary" size="lg" className="group" data-id="element-900">
                View All Courses
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-901" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-902">
            {popularCourses.map((course, index) => <motion.div key={course.id} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} data-id="element-903">
                <Link to={`/courses/${course.id}`} className="group block" data-id="element-904">
                  <div className="relative bg-white rounded-3xl overflow-hidden shadow-2xl hover:shadow-3xl transition-all duration-500 hover:-translate-y-2" data-id="element-905">
                    {/* Accent Bar */}
                    <div className="h-2 bg-gradient-to-r from-forest-500 via-ember-500 to-forest-500" data-id="element-906"></div>

                    <div className="p-8" data-id="element-907">
                      <div className="flex justify-between items-start mb-6" data-id="element-908">
                        <Badge variant="success" data-id="element-909">{course.category}</Badge>
                        <div className="text-right" data-id="element-910">
                          <p className="text-2xl font-display font-bold text-forest-900" data-id="element-911">
                            {course.price}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-912">per person</p>
                        </div>
                      </div>

                      <h3 className="text-2xl font-display font-bold text-forest-900 mb-3 group-hover:text-forest-700 transition-colors" data-id="element-913">
                        {course.title}
                      </h3>
                      <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-914">
                        {course.shortDescription}
                      </p>

                      <div className="space-y-3 mb-6" data-id="element-915">
                        <div className="flex items-center text-sm text-gray-600" data-id="element-916">
                          <CheckCircle2 className="w-4 h-4 mr-2 text-forest-600" data-id="element-917" />
                          {course.certification}
                        </div>
                        <div className="flex items-center text-sm text-gray-600" data-id="element-918">
                          <Users className="w-4 h-4 mr-2 text-forest-600" data-id="element-919" />
                          {course.delivery.join(' / ')}
                        </div>
                      </div>

                      <div className="flex items-center text-forest-700 font-semibold group-hover:text-ember-600 transition-colors" data-id="element-920">
                        Learn More
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" data-id="element-921" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Corporate CTA - Split Design */}
      <section className="relative overflow-hidden" data-id="element-922">
        <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-923">
          {/* Left: Image */}
          <div className="relative h-[400px] lg:h-auto" data-id="element-924">
            <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1932&q=80" alt="Team using online training platform" className="w-full h-full object-cover" data-id="element-925" />
            <div className="absolute inset-0 bg-gradient-to-r from-forest-900/90 to-forest-900/50" data-id="element-926"></div>
          </div>

          {/* Right: Content */}
          <div className="bg-gradient-to-br from-cream-50 to-white p-12 lg:p-20 flex items-center" data-id="element-927">
            <div data-id="element-928">
              <Badge variant="gradient" className="mb-6" data-id="element-929">
                <Building2 className="w-3 h-3 mr-1" data-id="element-930" />
                Team Accounts
              </Badge>

              <h2 className="text-4xl md:text-5xl font-display font-bold text-forest-900 mb-6" data-id="element-931">
                Train Your{' '}
                <span className="text-gradient" data-id="element-932">Entire Team Online</span>
              </h2>

              <p className="text-xl text-gray-700 mb-8 leading-relaxed" data-id="element-933">
                Manage your team's food safety compliance from one powerful
                online dashboard. Assign courses, track progress, and download
                certificates instantly.
              </p>

              <ul className="space-y-4 mb-10" data-id="element-934">
                {['Volume pricing for teams of 5+', 'Real-time progress tracking dashboard', 'Bulk enrolment via CSV upload', 'Instant certificate downloads for all staff'].map(item => <li key={item} className="flex items-start" data-id="element-935">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-forest-600 to-forest-700 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0" data-id="element-936">
                      <CheckCircle2 className="w-3 h-3 text-white" data-id="element-937" />
                    </div>
                    <span className="text-gray-700 font-medium" data-id="element-938">{item}</span>
                  </li>)}
              </ul>

              <div className="flex flex-col sm:flex-row gap-4" data-id="element-939">
                <Link to="/corporate" data-id="element-940">
                  <Button size="lg" variant="primary" data-id="element-941">
                    Explore Team Accounts
                  </Button>
                </Link>
                <Link to="/contact" data-id="element-942">
                  <Button size="lg" variant="outline" data-id="element-943">
                    Get a Quote
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 bg-white border-y border-forest-100" data-id="element-944">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-945">
          <p className="text-center text-sm font-bold text-forest-600 uppercase tracking-wider mb-8" data-id="element-946">
            Trusted by Ireland's Leading Brands
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 items-center opacity-40 grayscale" data-id="element-947">
            {['Hilton', 'SuperValu', 'Musgrave', 'Sodexo', 'Aramark'].map(brand => <div key={brand} className="text-center text-2xl font-display font-bold text-gray-800" data-id="element-948">
                  {brand}
                </div>)}
          </div>
        </div>
      </section>
    </div>;
}